import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { DENTAL_SPECIALTIES } from '../lib/constants';
import { generateConsumableUID, validateConsumableUID } from '../lib/helpers';
import ConsumableFullLabel from '../components/ConsumableFullLabel';
import ConsumableCompactLabel from '../components/ConsumableCompactLabel';
import BlankConsumableSheet from '../components/BlankConsumableSheet';
import CameraScanner from '../components/CameraScanner';

// ==========================================================
// หน้าตรวจสอบการตรวจรับพัสดุ & ระบบออก QR วัสดุสิ้นเปลือง
// (Material Receipt Inspection & Consumable QR System)
// ==========================================================
const ReceiptInspectionPage = ({
  inspectionReceiptId,
  setInspectionReceiptId,
  supabase,
  user,
  isMobile,
  onNavigate
}) => {
  // --- สถานะหลักของข้อมูลใบเสร็จ ---
  const [receiptsList, setReceiptsList] = useState([]);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // --- ตัวกรองค้นหา ---
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterSpecialty, setFilterSpecialty] = useState('all');

  // --- Modal ส่องภาพใบเสร็จขนาดใหญ่ ---
  const [zoomedImageUrl, setZoomedImageUrl] = useState(null);

  // --- Modal พิมพ์ฉลาก (Print Preview Modal) ---
  // printModalConfig: { isOpen: boolean, title: string, type: 'full' | 'compact' | 'blank_sheet', items: [] }
  const [printModalConfig, setPrintModalConfig] = useState({
    isOpen: false,
    title: '',
    type: 'full',
    items: []
  });

  // --- Modal สแกน QR เปล่าเพื่อผูกข้อมูล (Scan to Bind Modal) ---
  const [bindModalConfig, setBindModalConfig] = useState({
    isOpen: false,
    item: null,
    scannedCodes: [],
    manualInput: '',
    isCameraOpen: false,
    errorMsg: '',
    successMsg: '',
    isBinding: false
  });

  const manualInputRef = useRef(null);

  // ==========================================================
  // 1. ดึงข้อมูลใบเสร็จและรายการสินค้าจาก Supabase
  // ==========================================================
  const fetchReceipts = useCallback(async (targetReceiptId = null) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('receipts')
        .select(`
          *,
          receipt_items (*)
        `)
        .order('received_date', { ascending: false })
        .limit(150);

      if (error) throw error;

      const loadedReceipts = data || [];
      setReceiptsList(loadedReceipts);

      // จัดการเลือกใบเสร็จอัตโนมัติ
      const idToSelect = targetReceiptId || inspectionReceiptId;
      if (idToSelect) {
        const found = loadedReceipts.find(r => r.id === idToSelect);
        if (found) {
          setSelectedReceipt(found);
        } else if (loadedReceipts.length > 0) {
          setSelectedReceipt(loadedReceipts[0]);
        }
      } else if (loadedReceipts.length > 0 && !selectedReceipt) {
        setSelectedReceipt(loadedReceipts[0]);
      }
    } catch (err) {
      console.error('Error fetching receipts for inspection:', err);
      alert('เกิดข้อผิดพลาดในการโหลดข้อมูลใบเสร็จ: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  }, [supabase, inspectionReceiptId, selectedReceipt]);

  useEffect(() => {
    fetchReceipts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // เมื่อ inspectionReceiptId เปลี่ยนจากภายนอก
  useEffect(() => {
    if (inspectionReceiptId && receiptsList.length > 0) {
      const found = receiptsList.find(r => r.id === inspectionReceiptId);
      if (found) {
        setSelectedReceipt(found);
      }
    }
  }, [inspectionReceiptId, receiptsList]);

  // ==========================================================
  // 2. กรองรายการใบเสร็จ
  // ==========================================================
  const filteredReceipts = useMemo(() => {
    return receiptsList.filter(rc => {
      const q = searchQuery.toLowerCase().trim();
      const numMatch = !q || (rc.receipt_number && rc.receipt_number.toLowerCase().includes(q));
      const poMatch = !q || (rc.po_number && rc.po_number.toLowerCase().includes(q));
      const supMatch = !q || (rc.supplier_name && rc.supplier_name.toLowerCase().includes(q));
      const itemMatch = !q || (rc.receipt_items && rc.receipt_items.some(it =>
        it.item_name && it.item_name.toLowerCase().includes(q)
      ));

      // กรองวันที่
      let dateMatch = true;
      if (startDate && rc.received_date) {
        dateMatch = dateMatch && rc.received_date >= startDate;
      }
      if (endDate && rc.received_date) {
        dateMatch = dateMatch && rc.received_date <= endDate;
      }

      // กรองสาขา
      let specMatch = true;
      if (filterSpecialty && filterSpecialty !== 'all') {
        specMatch = rc.receipt_items && rc.receipt_items.some(it =>
          it.specialty === filterSpecialty || (DENTAL_SPECIALTIES.find(s => s.id === filterSpecialty)?.name === it.specialty)
        );
      }

      return (numMatch || poMatch || supMatch || itemMatch) && dateMatch && specMatch;
    });
  }, [receiptsList, searchQuery, startDate, endDate, filterSpecialty]);

  // ==========================================================
  // 3. คำนวณความคืบหน้าของใบเสร็จที่เลือก
  // ==========================================================
  const receiptMetrics = useMemo(() => {
    if (!selectedReceipt || !selectedReceipt.receipt_items) {
      return { totalItems: 0, totalUnits: 0, totalQrGenerated: 0, percent: 0, grandTotal: 0 };
    }
    const items = selectedReceipt.receipt_items;
    const totalItems = items.length;
    let totalUnits = 0;
    let totalQrGenerated = 0;
    let grandTotal = 0;

    items.forEach(it => {
      const qty = Number(it.quantity) || 0;
      const qrCount = Number(it.qr_generated_count) || 0;
      totalUnits += qty;
      totalQrGenerated += Math.min(qrCount, qty);
      grandTotal += Number(it.total_price) || 0;
    });

    const percent = totalUnits > 0 ? Math.round((totalQrGenerated / totalUnits) * 100) : 0;
    return { totalItems, totalUnits, totalQrGenerated, percent, grandTotal };
  }, [selectedReceipt]);

  // ==========================================================
  // 4. บันทึกข้อมูลสาขา (Specialty) และผู้รับผิดชอบ (Caretaker)
  // ==========================================================
  const handleUpdateItemField = async (itemId, field, value) => {
    if (!selectedReceipt) return;

    // อัปเดต state ทันทีเพื่อให้ UI ลื่นไหล
    const updatedItems = selectedReceipt.receipt_items.map(it => {
      if (it.id === itemId) {
        return { ...it, [field]: value };
      }
      return it;
    });

    setSelectedReceipt(prev => ({ ...prev, receipt_items: updatedItems }));

    // บันทึกลง Supabase
    try {
      const updatePayload = { [field]: value };
      const { error } = await supabase
        .from('receipt_items')
        .update(updatePayload)
        .eq('id', itemId);

      if (error) throw error;
    } catch (err) {
      console.error(`Error updating item ${field}:`, err);
    }
  };

  // ==========================================================
  // 5. พิมพ์เอกสาร / ฉลาก (Print Execution Helper)
  // ==========================================================
  const executeThermalPrint = (printableHtml) => {
    const printWindow = window.open('', '_blank', 'width=450,height=600');
    if (!printWindow) {
      alert('โปรดอนุญาต Pop-up บนเบราว์เซอร์ เพื่อเปิดหน้าต่างสำหรับพิมพ์สติ๊กเกอร์');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>พิมพ์ฉลากวัสดุสิ้นเปลือง - ทันตกรรม รพ.ฝาง</title>
          <style>
            @page {
              size: 50mm 100mm;
              margin: 0;
            }
            body {
              margin: 0;
              padding: 0;
              background-color: #ffffff;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            .print-container {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: flex-start;
              background: #ffffff;
            }
            .label-wrapper {
              page-break-after: always;
              break-after: page;
              display: block;
              margin: 0 auto;
              width: 200px;
              height: 400px;
              overflow: hidden;
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            ${printableHtml}
          </div>
          <script>
            window.onload = function() {
              window.focus();
              window.print();
              setTimeout(function() { window.close(); }, 800);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // ==========================================================
  // 6. การสร้างและพิมพ์ฉลาก (Full Label / Compact Label)
  // ==========================================================
  const handleGenerateAndPrint = async (item, labelType = 'full', quantityToPrint = null) => {
    if (!selectedReceipt || !item) return;

    const qty = quantityToPrint !== null ? quantityToPrint : (Number(item.quantity) || 1);
    if (qty <= 0) {
      alert('จำนวนที่ต้องการพิมพ์ต้องมากกว่า 0');
      return;
    }

    setIsSaving(true);
    try {
      const generatedItems = [];
      const consumableRows = [];
      const logRows = [];

      for (let i = 0; i < qty; i++) {
        const uid = generateConsumableUID(i);
        const itemData = {
          qr_code: uid,
          receipt_id: selectedReceipt.id,
          receipt_item_id: item.id,
          master_id: item.master_id || null,
          item_name: item.item_name,
          specialty: item.specialty || 'ทันตกรรมทั่วไป/อื่นๆ',
          lot_number: item.lot_number || null,
          exp_date: item.exp_date || null,
          mfg_date: item.mfg_date || null,
          unit: item.unit || 'ชิ้น',
          unit_price: Number(item.unit_price) || 0,
          total_price: Number(item.unit_price) || 0,
          caretaker_name: item.caretaker_name || null,
          current_location: item.warehouse_name || 'คลังใหญ่ (คลังพัสดุ)',
          status: 'active',
          label_type: labelType,
          created_by: user?.email || 'Admin',
          receipt_number: selectedReceipt.receipt_number,
          po_number: selectedReceipt.po_number
        };

        generatedItems.push(itemData);

        // แถวสำหรับบันทึกลง consumable_items
        consumableRows.push({
          qr_code: uid,
          receipt_id: selectedReceipt.id,
          receipt_item_id: item.id,
          master_id: item.master_id || null,
          item_name: item.item_name,
          specialty: item.specialty || 'ทันตกรรมทั่วไป/อื่นๆ',
          lot_number: item.lot_number || null,
          exp_date: item.exp_date || null,
          mfg_date: item.mfg_date || null,
          unit: item.unit || 'ชิ้น',
          unit_price: Number(item.unit_price) || 0,
          total_price: Number(item.unit_price) || 0,
          caretaker_name: item.caretaker_name || null,
          current_location: item.warehouse_name || 'คลังใหญ่ (คลังพัสดุ)',
          status: 'active',
          label_type: labelType,
          created_by: user?.email || 'Admin'
        });

        // แถวสำหรับ consumable_logs
        logRows.push({
          qr_code: uid,
          action: `สร้างฉลาก QR (${labelType === 'full' ? 'แบบเต็ม' : 'แบบย่อ'}) จากใบเสร็จ #${selectedReceipt.receipt_number}`,
          location: item.warehouse_name || 'คลังใหญ่ (คลังพัสดุ)',
          actor_name: user?.email || 'Admin',
          notes: `Lot: ${item.lot_number || '-'}, EXP: ${item.exp_date || '-'}`
        });
      }

      // บันทึกลงตาราง consumable_items
      const { error: insertErr } = await supabase
        .from('consumable_items')
        .insert(consumableRows);

      if (insertErr) throw insertErr;

      // บันทึกลงตาราง consumable_logs
      await supabase.from('consumable_logs').insert(logRows);

      // อัปเดต qr_generated_count ใน receipt_items
      const newCount = (Number(item.qr_generated_count) || 0) + qty;
      await supabase
        .from('receipt_items')
        .update({ qr_generated_count: newCount })
        .eq('id', item.id);

      // อัปเดต local state
      setSelectedReceipt(prev => ({
        ...prev,
        receipt_items: prev.receipt_items.map(it =>
          it.id === item.id ? { ...it, qr_generated_count: newCount } : it
        )
      }));

      // เปิด Preview Modal
      setPrintModalConfig({
        isOpen: true,
        title: `พิมพ์ฉลาก QR (${labelType === 'full' ? 'แบบเต็ม' : 'แบบย่อ'}): ${item.item_name} (${qty} ชิ้น)`,
        type: labelType,
        items: generatedItems
      });

    } catch (err) {
      console.error('Error generating consumable QR:', err);
      alert('เกิดข้อผิดพลาดในการสร้าง QR ฉลาก: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // ==========================================================
  // 7. พิมพ์ฉลากแบบชุด (Batch Print ทั้งใบเสร็จ)
  // ==========================================================
  const handleBatchPrint = async (labelType = 'full') => {
    if (!selectedReceipt || !selectedReceipt.receipt_items || selectedReceipt.receipt_items.length === 0) {
      alert('ไม่มีรายการสินค้าในใบเสร็จนี้');
      return;
    }

    const items = selectedReceipt.receipt_items;
    const confirmMsg = `ต้องการสร้างและพิมพ์ฉลาก QR (${labelType === 'full' ? 'แบบเต็ม' : 'แบบย่อ'}) สำหรับทุกรายการในใบเสร็จ #${selectedReceipt.receipt_number} หรือไม่?`;
    if (!window.confirm(confirmMsg)) return;

    setIsSaving(true);
    try {
      const allGenerated = [];
      const allConsumableRows = [];
      const allLogRows = [];
      const updatedCounts = {};

      let loopOffset = 0;
      for (const it of items) {
        const remainingQty = Math.max(1, (Number(it.quantity) || 1) - (Number(it.qr_generated_count) || 0));
        updatedCounts[it.id] = (Number(it.qr_generated_count) || 0) + remainingQty;

        for (let i = 0; i < remainingQty; i++) {
          const uid = generateConsumableUID(loopOffset++);
          const itemData = {
            qr_code: uid,
            receipt_id: selectedReceipt.id,
            receipt_item_id: it.id,
            master_id: it.master_id || null,
            item_name: it.item_name,
            specialty: it.specialty || 'ทันตกรรมทั่วไป/อื่นๆ',
            lot_number: it.lot_number || null,
            exp_date: it.exp_date || null,
            mfg_date: it.mfg_date || null,
            unit: it.unit || 'ชิ้น',
            unit_price: Number(it.unit_price) || 0,
            total_price: Number(it.unit_price) || 0,
            caretaker_name: it.caretaker_name || null,
            current_location: it.warehouse_name || 'คลังใหญ่ (คลังพัสดุ)',
            status: 'active',
            label_type: labelType,
            created_by: user?.email || 'Admin',
            receipt_number: selectedReceipt.receipt_number,
            po_number: selectedReceipt.po_number
          };

          allGenerated.push(itemData);

          allConsumableRows.push({
            qr_code: uid,
            receipt_id: selectedReceipt.id,
            receipt_item_id: it.id,
            master_id: it.master_id || null,
            item_name: it.item_name,
            specialty: it.specialty || 'ทันตกรรมทั่วไป/อื่นๆ',
            lot_number: it.lot_number || null,
            exp_date: it.exp_date || null,
            mfg_date: it.mfg_date || null,
            unit: it.unit || 'ชิ้น',
            unit_price: Number(it.unit_price) || 0,
            total_price: Number(it.unit_price) || 0,
            caretaker_name: it.caretaker_name || null,
            current_location: it.warehouse_name || 'คลังใหญ่ (คลังพัสดุ)',
            status: 'active',
            label_type: labelType,
            created_by: user?.email || 'Admin'
          });

          allLogRows.push({
            qr_code: uid,
            action: `สร้างฉลาก QR ชุด (${labelType === 'full' ? 'แบบเต็ม' : 'แบบย่อ'}) จากใบเสร็จ #${selectedReceipt.receipt_number}`,
            location: it.warehouse_name || 'คลังใหญ่ (คลังพัสดุ)',
            actor_name: user?.email || 'Admin',
            notes: `รายการ: ${it.item_name}`
          });
        }
      }

      if (allConsumableRows.length === 0) {
        alert('รายการสินค้าทั้งหมดในใบเสร็จนี้ถูกออก QR ครบแล้ว');
        setIsSaving(false);
        return;
      }

      // บันทึก DB
      const { error: batchErr } = await supabase.from('consumable_items').insert(allConsumableRows);
      if (batchErr) throw batchErr;
      await supabase.from('consumable_logs').insert(allLogRows);

      // อัปเดต counts
      for (const [id, count] of Object.entries(updatedCounts)) {
        await supabase.from('receipt_items').update({ qr_generated_count: count }).eq('id', id);
      }

      // อัปเดต state
      setSelectedReceipt(prev => ({
        ...prev,
        receipt_items: prev.receipt_items.map(it => ({
          ...it,
          qr_generated_count: updatedCounts[it.id] ?? it.qr_generated_count
        }))
      }));

      // เปิด Preview Modal
      setPrintModalConfig({
        isOpen: true,
        title: `พิมพ์ฉลาก QR ทั้งหมด (${labelType === 'full' ? 'แบบเต็ม' : 'แบบย่อ'}) (${allGenerated.length} ชิ้น)`,
        type: labelType,
        items: allGenerated
      });

    } catch (err) {
      console.error('Batch print error:', err);
      alert('เกิดข้อผิดพลาดในการพิมพ์ฉลากทั้งชุด: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // ==========================================================
  // 8. พิมพ์แผ่น QR เปล่าล่วงหน้า (Blank 8-Grid Sheet)
  // ==========================================================
  const handlePrintBlankSheet = () => {
    const blankItems = [];
    for (let i = 0; i < 8; i++) {
      blankItems.push({
        qr_code: generateConsumableUID(i),
        id: `blank-${i}`
      });
    }

    setPrintModalConfig({
      isOpen: true,
      title: '🖨️ พิมพ์แผ่นสติ๊กเกอร์ QR เปล่า (8 ช่อง ตาราง 4x2)',
      type: 'blank_sheet',
      items: blankItems
    });
  };

  // ==========================================================
  // 9. ระบบสแกน QR เปล่าเพื่อผูกกับรายการสินค้า (Scan to Bind)
  // ==========================================================
  const openBindModal = (item) => {
    setBindModalConfig({
      isOpen: true,
      item,
      scannedCodes: [],
      manualInput: '',
      isCameraOpen: false,
      errorMsg: '',
      successMsg: '',
      isBinding: false
    });
    setTimeout(() => {
      if (manualInputRef.current) manualInputRef.current.focus();
    }, 150);
  };

  // ตรวจสอบและเพิ่มรหัสที่สแกน
  const handleProcessScannedCode = async (rawCode) => {
    const code = rawCode.trim().toUpperCase();
    if (!code) return;

    setBindModalConfig(prev => ({ ...prev, errorMsg: '', successMsg: '' }));

    // 1. ตรวจสอบว่าขึ้นต้นด้วย CON- หรือไม่
    if (!validateConsumableUID(code)) {
      // ตรวจสอบกรณีเป็นรหัสเครื่องมือ Sterile (ตัวเลข 13-14 หลัก)
      if (/^\d{13,14}$/.test(code)) {
        setBindModalConfig(prev => ({
          ...prev,
          errorMsg: `❌ รหัส '${code}' เป็น QR ของเครื่องมือ Sterile ไม่ใช่สติ๊กเกอร์วัสดุสิ้นเปลือง (ต้องขึ้นต้นด้วย CON-)`,
          manualInput: ''
        }));
      } else {
        setBindModalConfig(prev => ({
          ...prev,
          errorMsg: `❌ รหัส '${code}' รูปแบบไม่ถูกต้อง! สติ๊กเกอร์วัสดุสิ้นเปลืองต้องขึ้นต้นด้วย 'CON-'`,
          manualInput: ''
        }));
      }
      return;
    }

    // 2. ตรวจสอบการสแกนซ้ำในชุดปัจจุบัน
    if (bindModalConfig.scannedCodes.includes(code)) {
      setBindModalConfig(prev => ({
        ...prev,
        errorMsg: `⚠️ รหัส '${code}' ถูกสแกนในรายการนี้แล้ว`,
        manualInput: ''
      }));
      return;
    }

    // 3. ตรวจสอบว่าถูกผูกในระบบ Supabase แล้วหรือไม่
    try {
      const { data: existing, error } = await supabase
        .from('consumable_items')
        .select('id, item_name, qr_code')
        .eq('qr_code', code)
        .maybeSingle();

      if (error) throw error;

      if (existing) {
        setBindModalConfig(prev => ({
          ...prev,
          errorMsg: `❌ รหัส '${code}' ถูกใช้งานแล้ว! ผูกอยู่กับ '${existing.item_name}'`,
          manualInput: ''
        }));
        return;
      }

      // สแกนสำเร็จ -> เพิ่มเข้ารายการ
      setBindModalConfig(prev => ({
        ...prev,
        scannedCodes: [...prev.scannedCodes, code],
        manualInput: '',
        successMsg: `✅ สแกนรหัส '${code}' สำเร็จ! (รวม ${prev.scannedCodes.length + 1} ชิ้น)`
      }));

    } catch (err) {
      console.error('Error checking code binding:', err);
      setBindModalConfig(prev => ({
        ...prev,
        errorMsg: 'เกิดข้อผิดพลาดในการตรวจสอบฐานข้อมูล: ' + err.message,
        manualInput: ''
      }));
    }
  };

  // กดยืนยันผูกข้อมูลเข้ากับรายการสินค้า
  const handleConfirmBinding = async () => {
    const { item, scannedCodes } = bindModalConfig;
    if (!item || scannedCodes.length === 0) {
      alert('กรุณาสแกนรหัสสติ๊กเกอร์อย่างน้อย 1 รหัสก่อนยืนยัน');
      return;
    }

    setBindModalConfig(prev => ({ ...prev, isBinding: true, errorMsg: '' }));
    try {
      const consumableRows = [];
      const logRows = [];

      scannedCodes.forEach(code => {
        consumableRows.push({
          qr_code: code,
          receipt_id: selectedReceipt.id,
          receipt_item_id: item.id,
          master_id: item.master_id || null,
          item_name: item.item_name,
          specialty: item.specialty || 'ทันตกรรมทั่วไป/อื่นๆ',
          lot_number: item.lot_number || null,
          exp_date: item.exp_date || null,
          mfg_date: item.mfg_date || null,
          unit: item.unit || 'ชิ้น',
          unit_price: Number(item.unit_price) || 0,
          total_price: Number(item.unit_price) || 0,
          caretaker_name: item.caretaker_name || null,
          current_location: item.warehouse_name || 'คลังใหญ่ (คลังพัสดุ)',
          status: 'active',
          label_type: 'blank_bound',
          created_by: user?.email || 'Admin'
        });

        logRows.push({
          qr_code: code,
          action: `ผูกสติ๊กเกอร์ QR เปล่ากับรายการสินค้าจากใบเสร็จ #${selectedReceipt.receipt_number}`,
          location: item.warehouse_name || 'คลังใหญ่ (คลังพัสดุ)',
          actor_name: user?.email || 'Admin',
          notes: `รายการ: ${item.item_name}, Lot: ${item.lot_number || '-'}`
        });
      });

      // บันทึกลง consumable_items
      const { error: insErr } = await supabase.from('consumable_items').insert(consumableRows);
      if (insErr) throw insErr;

      // บันทึกลง consumable_logs
      await supabase.from('consumable_logs').insert(logRows);

      // อัปเดต qr_generated_count ใน receipt_items
      const newCount = (Number(item.qr_generated_count) || 0) + scannedCodes.length;
      await supabase
        .from('receipt_items')
        .update({ qr_generated_count: newCount })
        .eq('id', item.id);

      // อัปเดต state ใบเสร็จ
      setSelectedReceipt(prev => ({
        ...prev,
        receipt_items: prev.receipt_items.map(it =>
          it.id === item.id ? { ...it, qr_generated_count: newCount } : it
        )
      }));

      alert(`✅ ผูกสติ๊กเกอร์ QR สำเร็จ ${scannedCodes.length} ชิ้น กับรายการ '${item.item_name}' เรียบร้อยแล้ว!`);
      setBindModalConfig(prev => ({ ...prev, isOpen: false, isBinding: false }));

    } catch (err) {
      console.error('Error confirming binding:', err);
      setBindModalConfig(prev => ({
        ...prev,
        isBinding: false,
        errorMsg: 'เกิดข้อผิดพลาดในการบันทึก: ' + err.message
      }));
    }
  };

  // ==========================================================
  // RENDER UI
  // ==========================================================
  return (
    <div style={{ maxWidth: '1440px', margin: '0 auto', padding: isMobile ? '12px' : '24px' }}>

      {/* แถบหัวเรื่องหน้าตรวจรับพัสดุ & ระบบออก QR */}
      <div
        style={{
          background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
          borderRadius: '16px',
          padding: isMobile ? '16px' : '24px',
          color: '#ffffff',
          marginBottom: '20px',
          boxShadow: '0 10px 25px -5px rgba(2, 132, 199, 0.3)',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '15px'
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1.8em' }}>📋</span>
            <div>
              <h2 style={{ margin: 0, fontSize: isMobile ? '1.3em' : '1.7em', fontWeight: 'bold' }}>
                ตรวจรับพัสดุ & ออก QR วัสดุสิ้นเปลือง
              </h2>
              <p style={{ margin: '4px 0 0 0', opacity: 0.9, fontSize: '0.9em' }}>
                ระบบตรวจรับเข้าคลัง แยกสาขาทันตกรรม และสร้างฉลาก QR Sticker ควบคุมรายชิ้น
              </p>
            </div>
          </div>
        </div>

        {/* ปุ่มลัดนำทาง */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {onNavigate && (
            <button
              onClick={() => onNavigate('receipt-stockin')}
              style={{
                background: 'rgba(255, 255, 255, 0.18)',
                color: '#ffffff',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '10px',
                padding: '8px 14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '0.9em',
                backdropFilter: 'blur(4px)'
              }}
            >
              <span>📥</span> ไปหน้าบันทึกใบเสร็จ
            </button>
          )}

          <button
            onClick={handlePrintBlankSheet}
            style={{
              background: '#ffffff',
              color: '#0369a1',
              border: 'none',
              borderRadius: '10px',
              padding: '8px 14px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '0.9em',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}
          >
            <span>🖨️</span> พิมพ์แผ่น QR เปล่า (8 ดวง)
          </button>
        </div>
      </div>

      {/* ส่วนควบคุม: เลือกใบเสร็จ และกรองข้อมูล */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '320px 1fr',
          gap: '20px',
          alignItems: 'start'
        }}
      >
        {/* ============================================================ */}
        {/* คอลัมน์ซ้าย: รายการใบเสร็จ (Receipts List / Selection) */}
        {/* ============================================================ */}
        <div
          style={{
            background: '#ffffff',
            borderRadius: '14px',
            border: '1px solid #e2e8f0',
            padding: '16px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '1.05em', color: '#1e293b', fontWeight: 'bold' }}>
              📑 รายการใบเสร็จ ({filteredReceipts.length})
            </h3>
            <button
              onClick={() => fetchReceipts()}
              title="โหลดข้อมูลใหม่"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#0284c7',
                fontSize: '1em'
              }}
            >
              🔄 รีเฟรช
            </button>
          </div>

          {/* ช่องค้นหา & ตัวกรอง */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
            <input
              type="text"
              placeholder="🔍 ค้นหาเลขที่, ผู้จำหน่าย, สินค้า..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '9px 12px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '0.88em',
                boxSizing: 'border-box'
              }}
            />

            {/* ตัวกรองช่วงวันที่ตรวจรับ */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
              <div>
                <span style={{ fontSize: '0.75em', color: '#64748b' }}>จากวันที่:</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '6px 8px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.8em',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <span style={{ fontSize: '0.75em', color: '#64748b' }}>ถึงวันที่:</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '6px 8px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    fontSize: '0.8em',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            {/* ตัวกรองสาขาทันตกรรม */}
            <div>
              <select
                value={filterSpecialty}
                onChange={(e) => setFilterSpecialty(e.target.value)}
                style={{
                  width: '100%',
                  padding: '7px 8px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  fontSize: '0.82em',
                  backgroundColor: '#ffffff',
                  color: '#334155'
                }}
              >
                <option value="all">🏷️ ทุกสาขาทันตกรรม</option>
                {DENTAL_SPECIALTIES.map(sp => (
                  <option key={sp.id} value={sp.name}>
                    {sp.code}: {sp.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* รายการการ์ดใบเสร็จ */}
          <div style={{ maxHeight: '680px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {isLoading ? (
              <div style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>
                ⏳ กำลังโหลดข้อมูลใบเสร็จ...
              </div>
            ) : filteredReceipts.length === 0 ? (
              <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>
                ไม่พบข้อมูลใบเสร็จที่ตรงกับคำค้นหา
              </div>
            ) : (
              filteredReceipts.map(rc => {
                const isSelected = selectedReceipt?.id === rc.id;
                const itemsCount = rc.receipt_items?.length || 0;
                return (
                  <div
                    key={rc.id}
                    onClick={() => setSelectedReceipt(rc)}
                    style={{
                      padding: '12px',
                      borderRadius: '10px',
                      border: isSelected ? '2px solid #0284c7' : '1px solid #e2e8f0',
                      backgroundColor: isSelected ? '#f0f9ff' : '#ffffff',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                      <span
                        style={{
                          fontWeight: 'bold',
                          color: isSelected ? '#0369a1' : '#1e293b',
                          fontSize: '0.95em'
                        }}
                      >
                        #{rc.receipt_number}
                      </span>
                      <span style={{ fontSize: '0.8em', color: '#64748b' }}>
                        📅 {rc.received_date || '-'}
                      </span>
                    </div>

                    <div style={{ fontSize: '0.86em', color: '#475569', marginBottom: '6px', fontWeight: '500' }}>
                      🏢 {rc.supplier_name || 'ไม่ระบุผู้จำหน่าย'}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.82em' }}>
                      <span style={{ color: '#64748b' }}>
                        📦 {itemsCount} รายการ
                      </span>
                      <span style={{ fontWeight: 'bold', color: '#059669' }}>
                        ฿{(Number(rc.total_amount) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* ============================================================ */}
        {/* คอลัมน์ขวา: รายละเอียดใบเสร็จที่เลือก (Selected Receipt Details) */}
        {/* ============================================================ */}
        <div>
          {selectedReceipt ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

              {/* 1. การ์ดส่วนหัวใบเสร็จ + สรุปทางการเงิน */}
              <div
                style={{
                  background: '#ffffff',
                  borderRadius: '14px',
                  border: '1px solid #e2e8f0',
                  padding: '18px 20px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}
              >
                {/* Header Information */}
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '12px',
                    borderBottom: '1px solid #f1f5f9',
                    paddingBottom: '14px',
                    marginBottom: '14px'
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span
                        style={{
                          background: '#e0f2fe',
                          color: '#0369a1',
                          fontWeight: 'bold',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '1.05em'
                        }}
                      >
                        ใบเสร็จ #{selectedReceipt.receipt_number}
                      </span>
                      {selectedReceipt.po_number && (
                        <span style={{ color: '#475569', fontSize: '0.9em', fontWeight: '600' }}>
                          (PO: {selectedReceipt.po_number})
                        </span>
                      )}
                    </div>

                    <div style={{ marginTop: '6px', fontSize: '0.92em', color: '#334155' }}>
                      🏢 ผู้จำหน่าย: <strong>{selectedReceipt.supplier_name || 'ไม่ระบุผู้จำหน่าย'}</strong>
                      <span style={{ margin: '0 8px', color: '#cbd5e1' }}>•</span>
                      📅 วันที่ตรวจรับ: <strong>{selectedReceipt.received_date || '-'}</strong>
                    </div>

                    {selectedReceipt.note && (
                      <div style={{ marginTop: '4px', fontSize: '0.85em', color: '#64748b' }}>
                        📝 บันทึก: {selectedReceipt.note}
                      </div>
                    )}
                  </div>

                  {/* ปุ่มดูรูปถ่ายเอกสารหลักฐาน */}
                  <div>
                    {selectedReceipt.receipt_image_url ? (
                      <button
                        type="button"
                        onClick={() => setZoomedImageUrl(selectedReceipt.receipt_image_url)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 14px',
                          borderRadius: '8px',
                          backgroundColor: '#f8fafc',
                          border: '1px solid #cbd5e1',
                          color: '#0284c7',
                          fontWeight: '600',
                          fontSize: '0.88em',
                          cursor: 'pointer'
                        }}
                      >
                        <span>🖼️</span> ดูภาพถ่ายใบเสร็จ
                      </button>
                    ) : (
                      <span style={{ fontSize: '0.82em', color: '#94a3b8' }}>ไม่มีภาพแนบ</span>
                    )}
                  </div>
                </div>

                {/* แถบสรุปสถานะการเงิน & ความคืบหน้าการออก QR */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                    gap: '14px'
                  }}
                >
                  {/* ยอดเงินรวม */}
                  <div
                    style={{
                      background: '#f8fafc',
                      borderRadius: '10px',
                      padding: '12px 14px',
                      border: '1px solid #e2e8f0'
                    }}
                  >
                    <div style={{ fontSize: '0.8em', color: '#64748b', marginBottom: '2px' }}>ยอดเงินรวมทั้งสิ้น</div>
                    <div style={{ fontSize: '1.35em', fontWeight: 'bold', color: '#059669' }}>
                      ฿{receiptMetrics.grandTotal.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>

                  {/* จำนวนรายการและชิ้น */}
                  <div
                    style={{
                      background: '#f8fafc',
                      borderRadius: '10px',
                      padding: '12px 14px',
                      border: '1px solid #e2e8f0'
                    }}
                  >
                    <div style={{ fontSize: '0.8em', color: '#64748b', marginBottom: '2px' }}>จำนวนรายการพัสดุ</div>
                    <div style={{ fontSize: '1.25em', fontWeight: 'bold', color: '#1e293b' }}>
                      {receiptMetrics.totalItems} รายการ{' '}
                      <span style={{ fontSize: '0.75em', color: '#64748b', fontWeight: 'normal' }}>
                        ({receiptMetrics.totalUnits} ชิ้น)
                      </span>
                    </div>
                  </div>

                  {/* ความคืบหน้าการสร้าง QR */}
                  <div
                    style={{
                      background: '#f8fafc',
                      borderRadius: '10px',
                      padding: '12px 14px',
                      border: '1px solid #e2e8f0'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8em', color: '#64748b', marginBottom: '4px' }}>
                      <span>ความคืบหน้าออก QR</span>
                      <strong style={{ color: receiptMetrics.percent === 100 ? '#059669' : '#0284c7' }}>
                        {receiptMetrics.percent}%
                      </strong>
                    </div>
                    {/* Progress Bar */}
                    <div style={{ height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                      <div
                        style={{
                          height: '100%',
                          width: `${receiptMetrics.percent}%`,
                          backgroundColor: receiptMetrics.percent === 100 ? '#10b981' : '#0284c7',
                          transition: 'width 0.3s ease'
                        }}
                      />
                    </div>
                    <div style={{ fontSize: '0.75em', color: '#64748b', marginTop: '4px' }}>
                      ออก QR แล้ว {receiptMetrics.totalQrGenerated} / {receiptMetrics.totalUnits} ชิ้น
                    </div>
                  </div>
                </div>

                {/* ปุ่มคำสั่งกลุ่ม (Batch Actions Bar) */}
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '10px',
                    marginTop: '16px',
                    paddingTop: '14px',
                    borderTop: '1px solid #f1f5f9'
                  }}
                >
                  <span style={{ fontSize: '0.88em', fontWeight: '600', color: '#475569' }}>
                    คำสั่งพิมพ์ทั้งชุด:
                  </span>

                  <button
                    onClick={() => handleBatchPrint('full')}
                    disabled={isSaving}
                    style={{
                      background: '#0284c7',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '7px 14px',
                      fontSize: '0.85em',
                      fontWeight: 'bold',
                      cursor: isSaving ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <span>🖨️</span> พิมพ์ QR ทั้งหมด (แบบเต็ม)
                  </button>

                  <button
                    onClick={() => handleBatchPrint('compact')}
                    disabled={isSaving}
                    style={{
                      background: '#475569',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '7px 14px',
                      fontSize: '0.85em',
                      fontWeight: 'bold',
                      cursor: isSaving ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <span>🏷️</span> พิมพ์ QR ทั้งหมด (แบบย่อ)
                  </button>
                </div>
              </div>

              {/* 2. ตารางรายการสินค้าในใบเสร็จ (Line Items Table) */}
              <div
                style={{
                  background: '#ffffff',
                  borderRadius: '14px',
                  border: '1px solid #e2e8f0',
                  padding: '16px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h3 style={{ margin: 0, fontSize: '1.05em', color: '#1e293b', fontWeight: 'bold' }}>
                    📦 รายการสินค้าสำหรับตรวจรับ & จัดทำ QR ({selectedReceipt.receipt_items?.length || 0} รายการ)
                  </h3>
                </div>

                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88em' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', color: '#475569', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ padding: '10px 8px', width: '35px', textAlign: 'center' }}>ลำดับ</th>
                        <th style={{ padding: '10px 10px' }}>ชื่อรายการ</th>
                        <th style={{ padding: '10px 10px', minWidth: '150px' }}>สาขาทันตกรรม</th>
                        <th style={{ padding: '10px 10px', minWidth: '130px' }}>ผู้รับผิดชอบ</th>
                        <th style={{ padding: '10px 8px' }}>Lot Number</th>
                        <th style={{ padding: '10px 8px' }}>EXP</th>
                        <th style={{ padding: '10px 8px', textAlign: 'right' }}>จำนวน</th>
                        <th style={{ padding: '10px 8px' }}>หน่วย</th>
                        <th style={{ padding: '10px 8px', textAlign: 'right' }}>ราคา/หน่วย</th>
                        <th style={{ padding: '10px 8px', textAlign: 'center' }}>สถานะ QR</th>
                        <th style={{ padding: '10px 10px', textAlign: 'center', minWidth: '220px' }}>จัดการ QR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedReceipt.receipt_items || []).map((it, idx) => {
                        const qty = Number(it.quantity) || 0;
                        const qrCount = Number(it.qr_generated_count) || 0;
                        const isComplete = qrCount >= qty && qty > 0;

                        return (
                          <tr
                            key={it.id || idx}
                            style={{
                              borderBottom: '1px solid #f1f5f9',
                              backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fafafa'
                            }}
                          >
                            {/* ลำดับ */}
                            <td style={{ padding: '10px 8px', textAlign: 'center', color: '#64748b' }}>
                              {idx + 1}
                            </td>

                            {/* ชื่อรายการ */}
                            <td style={{ padding: '10px 10px', fontWeight: 'bold', color: '#1e293b' }}>
                              {it.item_name}
                            </td>

                            {/* สาขาทันตกรรมเฉพาะทาง (Dropdown) */}
                            <td style={{ padding: '10px 10px' }}>
                              <select
                                value={it.specialty || 'general'}
                                onChange={(e) => handleUpdateItemField(it.id, 'specialty', e.target.value)}
                                style={{
                                  padding: '5px 8px',
                                  borderRadius: '6px',
                                  border: '1px solid #cbd5e1',
                                  fontSize: '0.88em',
                                  width: '100%',
                                  backgroundColor: '#ffffff',
                                  color: '#334155'
                                }}
                              >
                                {DENTAL_SPECIALTIES.map(sp => (
                                  <option key={sp.id} value={sp.name}>
                                    {sp.code}: {sp.name}
                                  </option>
                                ))}
                              </select>
                            </td>

                            {/* ผู้รับผิดชอบ (Caretaker) */}
                            <td style={{ padding: '10px 10px' }}>
                              <input
                                type="text"
                                placeholder="ผู้รับผิดชอบ..."
                                defaultValue={it.caretaker_name || ''}
                                onBlur={(e) => handleUpdateItemField(it.id, 'caretaker_name', e.target.value.trim())}
                                style={{
                                  padding: '5px 8px',
                                  borderRadius: '6px',
                                  border: '1px solid #cbd5e1',
                                  fontSize: '0.88em',
                                  width: '100%',
                                  boxSizing: 'border-box'
                                }}
                              />
                            </td>

                            {/* Lot */}
                            <td style={{ padding: '10px 8px' }}>
                              {it.lot_number ? (
                                <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: '4px', fontSize: '0.85em', fontWeight: 'bold' }}>
                                  {it.lot_number}
                                </span>
                              ) : '-'}
                            </td>

                            {/* EXP */}
                            <td style={{ padding: '10px 8px', fontSize: '0.85em', color: '#475569' }}>
                              {it.exp_date || '-'}
                            </td>

                            {/* จำนวน */}
                            <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 'bold' }}>
                              {qty.toLocaleString()}
                            </td>

                            {/* หน่วย */}
                            <td style={{ padding: '10px 8px', color: '#64748b' }}>
                              {it.unit || 'ชิ้น'}
                            </td>

                            {/* ราคา/หน่วย */}
                            <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                              {(Number(it.unit_price) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                            </td>

                            {/* สถานะ QR */}
                            <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                              <span
                                style={{
                                  display: 'inline-block',
                                  padding: '3px 8px',
                                  borderRadius: '12px',
                                  fontSize: '0.8em',
                                  fontWeight: 'bold',
                                  backgroundColor: isComplete ? '#dcfce7' : qrCount > 0 ? '#fef9c3' : '#f1f5f9',
                                  color: isComplete ? '#15803d' : qrCount > 0 ? '#a16207' : '#64748b'
                                }}
                              >
                                {qrCount} / {qty} ชิ้น
                              </span>
                            </td>

                            {/* Action Buttons: 3 QR Operations */}
                            <td style={{ padding: '10px 10px', textAlign: 'center' }}>
                              <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                {/* 1. พิมพ์ QR เต็ม */}
                                <button
                                  type="button"
                                  onClick={() => handleGenerateAndPrint(it, 'full')}
                                  title="สร้างและพิมพ์สติ๊กเกอร์ QR แบบเต็ม"
                                  style={{
                                    padding: '5px 8px',
                                    borderRadius: '6px',
                                    border: '1px solid #0284c7',
                                    backgroundColor: '#e0f2fe',
                                    color: '#0369a1',
                                    fontSize: '0.8em',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}
                                >
                                  🖨️ QR เต็ม
                                </button>

                                {/* 2. พิมพ์ QR ย่อ */}
                                <button
                                  type="button"
                                  onClick={() => handleGenerateAndPrint(it, 'compact')}
                                  title="สร้างและพิมพ์สติ๊กเกอร์ QR แบบกะทัดรัด (สำหรับหลอด/ขวดเล็ก)"
                                  style={{
                                    padding: '5px 8px',
                                    borderRadius: '6px',
                                    border: '1px solid #475569',
                                    backgroundColor: '#f1f5f9',
                                    color: '#334155',
                                    fontSize: '0.8em',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}
                                >
                                  🏷️ QR ย่อ
                                </button>

                                {/* 3. สแกน QR เปล่ามาผูก */}
                                <button
                                  type="button"
                                  onClick={() => openBindModal(it)}
                                  title="สแกนสติ๊กเกอร์ QR เปล่าที่พิมพ์ไว้แล้ว เพื่อนำมาผูกกับสินค้านี้"
                                  style={{
                                    padding: '5px 8px',
                                    borderRadius: '6px',
                                    border: '1px solid #059669',
                                    backgroundColor: '#ecfdf5',
                                    color: '#065f46',
                                    fontSize: '0.8em',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}
                                >
                                  📷 ผูก QR เปล่า
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          ) : (
            <div
              style={{
                background: '#ffffff',
                borderRadius: '14px',
                border: '1px dashed #cbd5e1',
                padding: '60px 20px',
                textAlign: 'center',
                color: '#64748b'
              }}
            >
              <div style={{ fontSize: '3em', marginBottom: '10px' }}>📋</div>
              <h3 style={{ margin: 0, color: '#334155' }}>กรุณาเลือกใบเสร็จจากรายการด้านซ้าย</h3>
              <p style={{ margin: '6px 0 0 0', fontSize: '0.9em' }}>
                เพื่อตรวจสอบรายการพัสดุและจัดทำ QR Code สติ๊กเกอร์สำหรับติดสินค้า
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ============================================================ */}
      {/* MODAL 1: พิมพ์ฉลาก (Print Preview Modal) */}
      {/* ============================================================ */}
      {printModalConfig.isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
              overflow: 'hidden'
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#f8fafc'
              }}
            >
              <h3 style={{ margin: 0, fontSize: '1.1em', color: '#1e293b', fontWeight: 'bold' }}>
                {printModalConfig.title}
              </h3>
              <button
                onClick={() => setPrintModalConfig({ isOpen: false, title: '', type: 'full', items: [] })}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.2em',
                  cursor: 'pointer',
                  color: '#64748b'
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Body: Scrollable Labels Preview */}
            <div
              id="printable-label-content"
              style={{
                padding: '20px',
                overflowY: 'auto',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '20px',
                justifyContent: 'center',
                backgroundColor: '#f1f5f9'
              }}
            >
              {printModalConfig.type === 'blank_sheet' ? (
                <div style={{ width: '200px', height: '400px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', background: '#fff' }}>
                  <BlankConsumableSheet items={printModalConfig.items} previewMode={true} />
                </div>
              ) : (
                printModalConfig.items.map((it, idx) => (
                  <div
                    key={it.qr_code || idx}
                    style={{
                      width: '200px',
                      height: '400px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      background: '#fff'
                    }}
                  >
                    {printModalConfig.type === 'compact' ? (
                      <ConsumableCompactLabel data={it} previewMode={true} />
                    ) : (
                      <ConsumableFullLabel data={it} previewMode={true} />
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: '16px 20px',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#ffffff'
              }}
            >
              <div style={{ fontSize: '0.88em', color: '#64748b' }}>
                ทั้งหมด <strong>{printModalConfig.items.length}</strong> แผ่น (ขนาดฉลาก 50x100 มม.)
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setPrintModalConfig({ isOpen: false, title: '', type: 'full', items: [] })}
                  style={{
                    padding: '10px 18px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    backgroundColor: '#ffffff',
                    color: '#475569',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  ปิด
                </button>

                <button
                  type="button"
                  onClick={() => {
                    const node = document.getElementById('printable-label-content');
                    if (node) {
                      executeThermalPrint(node.innerHTML);
                    }
                  }}
                  style={{
                    padding: '10px 22px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#0284c7',
                    color: '#ffffff',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 4px 6px rgba(2, 132, 199, 0.25)'
                  }}
                >
                  <span>🖨️</span> สั่งพิมพ์ฉลาก
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* MODAL 2: สแกน QR เปล่าเพื่อผูกข้อมูล (Scan to Bind Modal) */}
      {/* ============================================================ */}
      {bindModalConfig.isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.75)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              maxWidth: '520px',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
              overflow: 'hidden'
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#f8fafc'
              }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1em', color: '#1e293b', fontWeight: 'bold' }}>
                  📷 สแกน QR เปล่าเพื่อผูกกับรายการสินค้า
                </h3>
                <p style={{ margin: '2px 0 0 0', fontSize: '0.82em', color: '#64748b' }}>
                  รายการ: <strong>{bindModalConfig.item?.item_name}</strong>
                </p>
              </div>
              <button
                onClick={() => setBindModalConfig(prev => ({ ...prev, isOpen: false }))}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.2em',
                  cursor: 'pointer',
                  color: '#64748b'
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {/* รายละเอียดสินค้า */}
              <div
                style={{
                  background: '#f8fafc',
                  borderRadius: '10px',
                  padding: '12px',
                  border: '1px solid #e2e8f0',
                  fontSize: '0.88em'
                }}
              >
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                  <div>Lot: <strong>{bindModalConfig.item?.lot_number || '-'}</strong></div>
                  <div>EXP: <strong>{bindModalConfig.item?.exp_date || '-'}</strong></div>
                  <div>จำนวนทั้งหมด: <strong>{bindModalConfig.item?.quantity || 1} {bindModalConfig.item?.unit || 'ชิ้น'}</strong></div>
                  <div>ออก QR แล้ว: <strong>{bindModalConfig.item?.qr_generated_count || 0} ชิ้น</strong></div>
                </div>
              </div>

              {/* ช่องสแกนผ่าน Barcode Gun / คีย์บอร์ด */}
              <div>
                <label style={{ display: 'block', fontSize: '0.88em', fontWeight: 'bold', color: '#334155', marginBottom: '6px' }}>
                  ยิงบาร์โค้ดสติ๊กเกอร์ (Barcode Gun) หรือพิมพ์รหัส:
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    ref={manualInputRef}
                    type="text"
                    placeholder="เช่น CON-260907-1055001"
                    value={bindModalConfig.manualInput}
                    onChange={(e) => setBindModalConfig(prev => ({ ...prev, manualInput: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleProcessScannedCode(bindModalConfig.manualInput);
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      fontSize: '0.95em',
                      fontFamily: 'monospace'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => handleProcessScannedCode(bindModalConfig.manualInput)}
                    style={{
                      padding: '10px 16px',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: '#0284c7',
                      color: '#ffffff',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    เพิ่ม
                  </button>
                </div>
              </div>

              {/* ปุ่มเปิดกล้องสแกนสำหรับมือถือ/แท็บเล็ต */}
              <div>
                <button
                  type="button"
                  onClick={() => setBindModalConfig(prev => ({ ...prev, isCameraOpen: true }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px dashed #0284c7',
                    backgroundColor: '#f0f9ff',
                    color: '#0284c7',
                    fontWeight: 'bold',
                    fontSize: '0.9em',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <span>📷</span> เปิดกล้องสแกน QR Code (Camera Scanner)
                </button>
              </div>

              {/* ข้อความแจ้งเตือน Error หรือ Success */}
              {bindModalConfig.errorMsg && (
                <div
                  style={{
                    background: '#fef2f2',
                    color: '#b91c1c',
                    border: '1px solid #fecaca',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    fontSize: '0.85em',
                    fontWeight: '500'
                  }}
                >
                  {bindModalConfig.errorMsg}
                </div>
              )}

              {bindModalConfig.successMsg && (
                <div
                  style={{
                    background: '#f0fdf4',
                    color: '#15803d',
                    border: '1px solid #bbf7d0',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    fontSize: '0.85em',
                    fontWeight: '500'
                  }}
                >
                  {bindModalConfig.successMsg}
                </div>
              )}

              {/* รายการรหัสที่สแกนแล้วในรอบนี้ */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span style={{ fontSize: '0.88em', fontWeight: 'bold', color: '#1e293b' }}>
                    รหัสที่สแกนในชุดนี้ ({bindModalConfig.scannedCodes.length} ชิ้น):
                  </span>
                  {bindModalConfig.scannedCodes.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setBindModalConfig(prev => ({ ...prev, scannedCodes: [] }))}
                      style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '0.8em', cursor: 'pointer' }}
                    >
                      ล้างทั้งหมด
                    </button>
                  )}
                </div>

                <div
                  style={{
                    maxHeight: '160px',
                    overflowY: 'auto',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '8px',
                    background: '#f8fafc'
                  }}
                >
                  {bindModalConfig.scannedCodes.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.85em', padding: '16px' }}>
                      ยังไม่มีรหัสที่สแกน สแกนสติ๊กเกอร์ CON- เพื่อผูกข้อมูล
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {bindModalConfig.scannedCodes.map((code, idx) => (
                        <div
                          key={code}
                          style={{
                            background: '#ffffff',
                            border: '1px solid #cbd5e1',
                            borderRadius: '6px',
                            padding: '4px 8px',
                            fontSize: '0.82em',
                            fontFamily: 'monospace',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <span>{code}</span>
                          <button
                            type="button"
                            onClick={() => {
                              setBindModalConfig(prev => ({
                                ...prev,
                                scannedCodes: prev.scannedCodes.filter(c => c !== code)
                              }));
                            }}
                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 0 }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: '16px 20px',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
                background: '#f8fafc'
              }}
            >
              <button
                type="button"
                onClick={() => setBindModalConfig(prev => ({ ...prev, isOpen: false }))}
                disabled={bindModalConfig.isBinding}
                style={{
                  padding: '10px 18px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  backgroundColor: '#ffffff',
                  color: '#475569',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                ยกเลิก
              </button>

              <button
                type="button"
                onClick={handleConfirmBinding}
                disabled={bindModalConfig.isBinding || bindModalConfig.scannedCodes.length === 0}
                style={{
                  padding: '10px 22px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: '#059669',
                  color: '#ffffff',
                  fontWeight: 'bold',
                  cursor: bindModalConfig.isBinding || bindModalConfig.scannedCodes.length === 0 ? 'not-allowed' : 'pointer',
                  opacity: bindModalConfig.scannedCodes.length === 0 ? 0.6 : 1
                }}
              >
                {bindModalConfig.isBinding ? '⏳ กำลังบันทึก...' : `ยืนยันผูกข้อมูล (${bindModalConfig.scannedCodes.length} ชิ้น)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* กล้องสแกนเนอร์ (เมื่อเปิดใช้งานใน Bind Modal) */}
      {bindModalConfig.isCameraOpen && (
        <CameraScanner
          onScanSuccess={(decodedText) => {
            handleProcessScannedCode(decodedText);
            setBindModalConfig(prev => ({ ...prev, isCameraOpen: false }));
          }}
          onClose={() => setBindModalConfig(prev => ({ ...prev, isCameraOpen: false }))}
        />
      )}

      {/* ============================================================ */}
      {/* MODAL 3: ซูมดูภาพถ่ายใบเสร็จ (Zoom Image Modal) */}
      {/* ============================================================ */}
      {zoomedImageUrl && (
        <div
          onClick={() => setZoomedImageUrl(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
            cursor: 'zoom-out',
            padding: '20px'
          }}
        >
          <img
            src={zoomedImageUrl}
            alt="Receipt High-Resolution"
            style={{
              maxWidth: '95vw',
              maxHeight: '95vh',
              objectFit: 'contain',
              borderRadius: '8px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
            }}
          />
        </div>
      )}

    </div>
  );
};

export default ReceiptInspectionPage;
